import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { PenLine, Plus, Trash2, Eye, LogOut, ExternalLink, UserRound, Store, Mail, Star, ArrowUp, ArrowDown, Bell, MessageCircle, TrendingUp, Map, GripVertical, Sparkles, Archive, Search, X, Rabbit } from "lucide-react";
import { formatDate } from "@/lib/format";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [unread, setUnread] = useState(0);
  const [online, setOnline] = useState(0);
  const [onlinePages, setOnlinePages] = useState([]);
  const [peakToday, setPeakToday] = useState(0);

  const loadPresence = () => {
    api
      .get("/admin/presence")
      .then((res) => {
        setOnline(res.data.online || 0);
        setOnlinePages(res.data.pages || []);
        setPeakToday(res.data.peak_today || 0);
      })
      .catch(() => {});
  };

  const loadComments = () => {
    api
      .get("/admin/comments")
      .then((res) => {
        setComments(res.data.comments || []);
        setUnread(res.data.unread || 0);
      })
      .catch(() => {});
  };

  const markRead = async () => {
    try {
      await api.post("/admin/comments/mark-read");
      setUnread(0);
      setComments((cs) => cs.map((c) => ({ ...c, read: true })));
    } catch {
      toast.error("Errore");
    }
  };

  const load = () => {
    setLoading(true);
    api
      .get("/admin/posts")
      .then((res) => setPosts(res.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    loadComments();
    loadPresence();
    const iv = setInterval(loadPresence, 15000);
    return () => clearInterval(iv);
  }, []);

  const doLogout = () => {
    logout();
    navigate("/");
  };

  const remove = async (id) => {
    try {
      await api.delete(`/posts/${id}`);
      setPosts((p) => p.filter((x) => x.id !== id));
      toast.success("Articolo eliminato");
    } catch {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const toggleFeature = async (id) => {
    try {
      await api.post(`/posts/${id}/feature`);
      await load();
      toast.success("Evidenza aggiornata");
    } catch {
      toast.error("Errore");
    }
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= posts.length) return;
    const arr = [...posts];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setPosts(arr);
    try {
      await api.post("/posts/reorder", { ids: arr.map((p) => p.id) });
    } catch {
      toast.error("Errore nel riordino");
      load();
    }
  };

  const published = posts.filter((p) => p.status === "published").length;
  const totalViews = posts.reduce((s, p) => s + (p.views || 0), 0);

  const [dragIndex, setDragIndex] = useState(null);
  const [search, setSearch] = useState("");

  const persistOrder = async (arr) => {
    setPosts(arr);
    try {
      await api.post("/posts/reorder", { ids: arr.map((p) => p.id) });
    } catch {
      toast.error("Errore nel riordino");
      load();
    }
  };

  const onDrop = (i) => {
    if (dragIndex === null || dragIndex === i) {
      setDragIndex(null);
      return;
    }
    const arr = [...posts];
    const [moved] = arr.splice(dragIndex, 1);
    arr.splice(i, 0, moved);
    setDragIndex(null);
    persistOrder(arr);
  };

  const deleteComment = async (id) => {
    try {
      await api.delete(`/comments/${id}`);
      setComments((cs) => cs.filter((c) => c.id !== id));
      toast.success("Commento eliminato");
    } catch {
      toast.error("Errore");
    }
  };

  return (
    <div className="relative z-10 min-h-screen bg-[#F9F8F6]">
      {/* Top bar */}
      <div className="border-b border-[#E7E5E4] bg-[#F2F0EB]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
          <div>
            <span className="font-display text-2xl tracking-tight text-[#1C1917]">
              Area autore
            </span>
            <p className="label-ui text-[#57534E] mt-1">Ciao, {user?.name || "Fabio"}</p>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="#notifiche"
              data-testid="notifications-bell"
              title="Notifiche commenti"
              className="relative label-ui text-[#57534E] hover:text-[#9F583F] transition-colors inline-flex items-center gap-2"
            >
              <Bell size={15} strokeWidth={1.5} />
              {unread > 0 && (
                <span
                  data-testid="notifications-badge"
                  className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-[#9F583F] text-white text-[10px] font-ui leading-none"
                >
                  {unread}
                </span>
              )}
            </a>
            <Link
              to="/admin/messaggi"
              className="label-ui text-[#57534E] hover:text-[#9F583F] transition-colors hidden sm:inline-flex items-center gap-2"
              data-testid="view-messages-link"
            >
              <Mail size={13} strokeWidth={1.5} /> Messaggi
            </Link>
            <Link
              to="/admin/sponsor"
              className="label-ui text-[#57534E] hover:text-[#9F583F] transition-colors hidden sm:inline-flex items-center gap-2"
              data-testid="manage-sponsors-link"
            >
              <Store size={13} strokeWidth={1.5} /> Sponsor
            </Link>
            <Link
              to="/admin/categorie"
              className="label-ui text-[#57534E] hover:text-[#9F583F] transition-colors hidden sm:inline-flex items-center gap-2"
              data-testid="manage-categories-link"
            >
              <Map size={13} strokeWidth={1.5} /> Categorie
            </Link>
            <Link
              to="/admin/consigli"
              className="label-ui text-[#57534E] hover:text-[#9F583F] transition-colors hidden sm:inline-flex items-center gap-2"
              data-testid="manage-recommendations-link"
            >
              <Sparkles size={13} strokeWidth={1.5} /> Consigli
            </Link>
            <Link
              to="/admin/cassetto"
              className="label-ui text-[#57534E] hover:text-[#9F583F] transition-colors hidden sm:inline-flex items-center gap-2"
              data-testid="manage-cassetto-link"
            >
              <Archive size={13} strokeWidth={1.5} /> Il Cassetto
            </Link>
            <Link
              to="/admin/giraffa-perduta"
              className="label-ui text-[#57534E] hover:text-[#9F583F] transition-colors hidden sm:inline-flex items-center gap-2"
              data-testid="manage-lost-giraffe-link"
            >
              <Rabbit size={13} strokeWidth={1.5} /> Giraffa Perduta
            </Link>
            <Link
              to="/admin/chi-sono"
              className="label-ui text-[#57534E] hover:text-[#9F583F] transition-colors hidden sm:inline-flex items-center gap-2"
              data-testid="edit-about-link"
            >
              <UserRound size={13} strokeWidth={1.5} /> Chi sono
            </Link>
            <Link
              to="/"
              target="_blank"
              className="label-ui text-[#57534E] hover:text-[#9F583F] transition-colors hidden sm:inline-flex items-center gap-2"
              data-testid="view-site-link"
            >
              Vedi il blog <ExternalLink size={13} strokeWidth={1.5} />
            </Link>
            <Button
              onClick={doLogout}
              data-testid="logout-button"
              variant="ghost"
              className="rounded-none label-ui text-[#57534E] hover:text-[#9F583F] hover:bg-transparent"
            >
              <LogOut size={15} strokeWidth={1.5} className="mr-2" /> Esci
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 md:px-10 py-12">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-6 mb-12">
          {[
            { label: "Articoli", value: posts.length },
            { label: "Pubblicati", value: published },
            { label: "Letture totali", value: totalViews },
          ].map((s) => (
            <div key={s.label} className="border border-[#E7E5E4] p-6 bg-[#F2F0EB]">
              <p className="font-display text-4xl text-[#1C1917]">{s.value}</p>
              <p className="label-ui text-[#57534E] mt-2">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Utenti online in tempo reale */}
        <div className="mb-12 border border-[#E7E5E4] bg-[#F2F0EB]" data-testid="presence-panel">
          <div className="flex items-center gap-3 px-6 py-4 border-b border-[#E7E5E4]">
            <span className="relative flex h-3 w-3">
              {online > 0 && (
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-60" />
              )}
              <span className={`relative inline-flex rounded-full h-3 w-3 ${online > 0 ? "bg-green-600" : "bg-[#A8A29E]"}`} />
            </span>
            <h2 className="font-display text-2xl tracking-tight text-[#1C1917]">Online ora</h2>
            <span className="font-display text-2xl text-[#9F583F]" data-testid="online-count">{online}</span>
            <span className="label-ui text-[#57534E] ml-auto flex items-center gap-1.5" data-testid="peak-today" title="Numero massimo di persone online oggi">
              <TrendingUp size={14} strokeWidth={1.5} className="text-[#9F583F]" />
              Picco oggi: {peakToday}
            </span>
          </div>

          {online === 0 ? (
            <p className="font-body italic text-[#57534E] px-6 py-6">
              Nessun visitatore sul sito in questo momento.
            </p>
          ) : (
            <div className="divide-y divide-[#E7E5E4]">
              {onlinePages.map((pg) => (
                <div
                  key={pg.label}
                  data-testid="online-page-row"
                  className="flex items-center justify-between gap-4 px-6 py-3"
                >
                  <span className="font-body text-[#292524] truncate">{pg.label}</span>
                  <span className="label-ui text-[#57534E] shrink-0">
                    {pg.count} {pg.count === 1 ? "persona" : "persone"}
                  </span>
                </div>
              ))}
            </div>
          )}
          <p className="label-ui text-[#A8A29E] px-6 py-2 border-t border-[#E7E5E4]">
            Aggiornato ogni 15 secondi
          </p>
        </div>

        {/* Notifiche commenti */}
        <div id="notifiche" className="mb-12 border border-[#E7E5E4] bg-[#F2F0EB]" data-testid="notifications-panel">
          <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-[#E7E5E4]">
            <div className="flex items-center gap-3">
              <Bell size={17} strokeWidth={1.5} className="text-[#9F583F]" />
              <h2 className="font-display text-2xl tracking-tight text-[#1C1917]">
                Notifiche
              </h2>
              {unread > 0 && (
                <span className="label-ui px-2 py-0.5 rounded-full bg-[#9F583F] text-white">
                  {unread} {unread === 1 ? "nuovo" : "nuovi"}
                </span>
              )}
            </div>
            {unread > 0 && (
              <Button
                onClick={markRead}
                data-testid="mark-read-button"
                variant="ghost"
                className="rounded-none label-ui text-[#57534E] hover:text-[#9F583F] hover:bg-transparent h-auto py-2"
              >
                Segna tutti come letti
              </Button>
            )}
          </div>

          {comments.length === 0 ? (
            <p className="font-body italic text-[#57534E] px-6 py-8">
              Nessun commento ancora. Quando un lettore commenta un articolo, lo vedrai qui.
            </p>
          ) : (
            <div className="divide-y divide-[#E7E5E4]">
              {comments.slice(0, 8).map((c) => (
                <div
                  key={c.id}
                  data-testid={`notification-comment-${c.id}`}
                  className={`flex items-start justify-between gap-4 px-6 py-4 ${!c.read ? "bg-[#FBF6F3]" : ""}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {!c.read && <span className="w-2 h-2 rounded-full bg-[#9F583F] shrink-0" />}
                      <span className="font-display text-lg text-[#1C1917]">{c.name}</span>
                      <span className="label-ui text-[#A8A29E]">{formatDate(c.created_at)}</span>
                    </div>
                    <p className="font-body text-[#292524] leading-relaxed line-clamp-2">{c.content}</p>
                    <div className="flex items-center gap-2 mt-1 label-ui text-[#57534E]">
                      <MessageCircle size={12} strokeWidth={1.5} />
                      {c.post_slug ? (
                        <Link to={`/articolo/${c.post_slug}`} target="_blank" className="hover:text-[#9F583F] transition-colors truncate">
                          {c.post_title}
                        </Link>
                      ) : (
                        <span className="truncate">{c.post_title}</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => deleteComment(c.id)}
                    data-testid={`delete-notification-${c.id}`}
                    title="Elimina commento"
                    className="text-[#A8A29E] hover:text-red-600 transition-colors shrink-0"
                  >
                    <Trash2 size={15} strokeWidth={1.5} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
          <h2 className="font-display text-3xl tracking-tight text-[#1C1917]">I tuoi articoli</h2>
          <Button
            onClick={() => navigate("/admin/scrivi")}
            data-testid="new-post-button"
            className="rounded-none bg-[#9F583F] hover:bg-[#1C1917] text-white label-ui px-6 py-6 h-auto transition-colors"
          >
            <Plus size={16} strokeWidth={2} className="mr-2" /> Scrivi articolo
          </Button>
        </div>

        {!loading && posts.length > 0 && (
          <div className="relative mb-8 max-w-md">
            <Search size={17} strokeWidth={1.6} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca un articolo per titolo…"
              data-testid="admin-post-search"
              className="w-full font-ui text-sm border border-[#E7E5E4] bg-[#F2F0EB] pl-10 pr-9 py-2.5 focus:outline-none focus:border-[#9F583F] transition-colors"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                data-testid="admin-post-search-clear"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A8A29E] hover:text-[#9F583F]"
                aria-label="Cancella ricerca"
              >
                <X size={15} />
              </button>
            )}
          </div>
        )}

        {loading ? (
          <p className="font-ui text-sm text-[#57534E] py-10">Caricamento…</p>
        ) : posts.length === 0 ? (
          <div className="border border-dashed border-[#E7E5E4] py-20 text-center">
            <p className="font-display text-2xl italic text-[#57534E]">
              Non hai ancora scritto nulla.
            </p>
            <Button
              onClick={() => navigate("/admin/scrivi")}
              className="mt-6 rounded-none bg-[#1C1917] hover:bg-[#9F583F] text-white label-ui px-6 py-5 h-auto transition-colors"
            >
              Scrivi il primo articolo
            </Button>
          </div>
        ) : (() => {
          const term = search.trim().toLowerCase();
          const visiblePosts = term
            ? posts.filter((p) => (p.title || "").toLowerCase().includes(term) || (p.slug || "").toLowerCase().includes(term))
            : posts;
          if (visiblePosts.length === 0) {
            return (
              <div className="border border-dashed border-[#E7E5E4] py-16 text-center" data-testid="admin-search-empty">
                <p className="font-display text-xl italic text-[#57534E]">Nessun articolo trovato per “{search}”.</p>
              </div>
            );
          }
          return (
          <div className="border-t border-[#E7E5E4]">
            {term && (
              <p className="label-ui text-[#A8A29E] py-3">{visiblePosts.length} risultat{visiblePosts.length === 1 ? "o" : "i"} · trascinamento disattivato durante la ricerca</p>
            )}
            {visiblePosts.map((p, i) => (
              <div
                key={p.id}
                data-testid={`admin-post-${p.slug}`}
                draggable={!term}
                onDragStart={() => !term && setDragIndex(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => !term && onDrop(i)}
                onDragEnd={() => setDragIndex(null)}
                className={`flex items-center justify-between gap-4 py-5 border-b border-[#E7E5E4] transition-opacity ${term ? "" : "cursor-grab active:cursor-grabbing"} ${dragIndex === i ? "opacity-40" : ""}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <GripVertical size={18} strokeWidth={1.5} className="text-[#C9C2BA] shrink-0" data-testid={`drag-handle-${p.slug}`} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <span
                        className={`label-ui px-2 py-0.5 ${
                          p.status === "published"
                            ? "text-[#4d7c3f] border border-[#c5d9bb]"
                            : "text-[#9F583F] border border-[#e6cbc0]"
                        }`}
                      >
                        {p.status === "published" ? "Pubblicato" : "Bozza"}
                      </span>
                      {p.featured && (
                        <span className="label-ui px-2 py-0.5 text-[#9F583F] border border-[#e6cbc0] inline-flex items-center gap-1">
                          <Star size={11} fill="#9F583F" /> In evidenza
                        </span>
                      )}
                      <span className="label-ui text-[#A8A29E] hidden sm:inline-flex items-center gap-1">
                        <Eye size={12} /> {p.views || 0}
                      </span>
                    </div>
                    <h3 className="font-display text-xl md:text-2xl tracking-tight text-[#1C1917] truncate">
                      {p.title}
                    </h3>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <div className="flex flex-col">
                    <button onClick={() => move(i, -1)} disabled={i === 0} title="Sposta su" className="text-[#A8A29E] hover:text-[#1C1917] disabled:opacity-30">
                      <ArrowUp size={15} strokeWidth={1.5} />
                    </button>
                    <button onClick={() => move(i, 1)} disabled={i === posts.length - 1} title="Sposta giù" className="text-[#A8A29E] hover:text-[#1C1917] disabled:opacity-30">
                      <ArrowDown size={15} strokeWidth={1.5} />
                    </button>
                  </div>
                  <button
                    onClick={() => toggleFeature(p.id)}
                    data-testid={`feature-post-${p.slug}`}
                    title={p.featured ? "Togli dall'evidenza" : "Metti in evidenza"}
                    className={`w-10 h-10 flex items-center justify-center transition-colors ${p.featured ? "text-[#9F583F]" : "text-[#57534E] hover:text-[#9F583F]"}`}
                  >
                    <Star size={17} strokeWidth={1.5} fill={p.featured ? "#9F583F" : "none"} />
                  </button>
                  {p.status === "published" && (
                    <Link
                      to={`/articolo/${p.slug}`}
                      target="_blank"
                      className="w-10 h-10 flex items-center justify-center text-[#57534E] hover:text-[#9F583F] transition-colors"
                      title="Vedi"
                    >
                      <Eye size={17} strokeWidth={1.5} />
                    </Link>
                  )}
                  <button
                    onClick={() => navigate(`/admin/scrivi/${p.id}`)}
                    data-testid={`edit-post-${p.slug}`}
                    className="w-10 h-10 flex items-center justify-center text-[#57534E] hover:text-[#9F583F] transition-colors"
                    title="Modifica"
                  >
                    <PenLine size={17} strokeWidth={1.5} />
                  </button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        data-testid={`delete-post-${p.slug}`}
                        className="w-10 h-10 flex items-center justify-center text-[#57534E] hover:text-red-600 transition-colors"
                        title="Elimina"
                      >
                        <Trash2 size={17} strokeWidth={1.5} />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-none bg-[#F9F8F6] border-[#E7E5E4]">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="font-display text-2xl">
                          Eliminare l'articolo?
                        </AlertDialogTitle>
                        <AlertDialogDescription className="font-body">
                          "{p.title}" verrà rimosso definitivamente insieme ai suoi commenti.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none label-ui">Annulla</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => remove(p.id)}
                          data-testid="confirm-delete-button"
                          className="rounded-none bg-red-700 hover:bg-red-800 label-ui"
                        >
                          Elimina
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
          );
        })()}
      </div>
    </div>
  );
}
